import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import "../../../css/CreatorDashboardMassTextsPanel.css";
import { Icon } from "@iconify/react";
import { Colors } from "../../../utils/colors";
import {
  MARKITAI,
  MARKIT_DOCS,
  hasSubscription,
  isBusinessSubscription,
  mostRecentSubscription,
  mostRecentSubscriptionPlan,
} from "@markit/common.utils";
import GradientButton from "../../Buttons/GradientButton";
import { RWebShare } from "react-web-share";
import { GetLongDate } from "../../../utils/GetLongDate";
import RectangleButton from "../../Buttons/RectangleButton";
import PopupModalContainer from "../../Containers/PopupModalContainer";
import { isDesktop } from "react-device-detect";
import { onShareLink } from "../../../utils/shareLink";
import PaymentFailedButtonModal from "../../DisplayModal/UpdatePaymentInfoModal";
import AlertContainer from "../../Containers/AlertContainer";
import { useNavigate } from "../../../hooks/useNavigate";
import StandardBorderedContainer from "../../Containers/StandardBorderedContainer";
import EditProfileConvertModal from "./EditProfileConvertModal";
import SubscriptionPlanLabelItem from "../../Subscription/SubscriptionPlanLabelItem";
import PaymentFailedButton from "../../Subscription/PaymentFailedButton";
import { CircularProgress } from "@mui/material";
import DeleteAccountModal from "../../DisplayModal/DeleteAccountModal";
import ProfilePic from "../../ProfilePic";
import ProfilePageEditProfile from "../../ProfilePage/ProfilePageEditProfile";
import { PlanTypeV4, TeamMemberRole } from "@markit/common.types";
import { NavigationId } from "../../../navigation/AppParamList";
import { onCreatorPanelNavigatePath } from "../../../utils/navigationUtils";
import EnablePaidTicketsModal from "../../DisplayModal/EnablePaidTicketsModal";
import { testStripe } from "../../../utils/stripeAccountUtils";
import { isGoogleAuthUser } from "../../../utils/ReauthenticateLogin";
import { notificationActions } from "../../../redux/slices/notificationSlice";

type ProfileAccountDetailsProps = {
  setTabValue?: (value: number) => void;
};

const ProfileAccountDetails = (props: ProfileAccountDetailsProps) => {
  const { setTabValue } = props;
  const { account } = useSelector(getAccountState);
  const {
    accountData,
    teamMemberAccountData,
    teamMemberData,
    readyForPayment,
  } = account;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [paymentFailedVisible, setPaymentFailedVisible] = useState(false);
  const [convertModal, setConvertModal] = useState(false);
  const [changeModal, setChangeModal] = useState(false);
  const [passwordModal, setPasswordModal] = useState(false);
  const [connectStripeModal, setConnectStripeModal] = useState(false);
  const [deleteAccountModal, setDeleteAccountModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);

  const [alertText, setAlertText] = useState({ heading: "", subHeading: "" });

  const styles = {
    container: {
      paddingBlock: 14,
      borderRadius: 14,
      backgroundColor: Colors.WHITE,
    },
    subtext: { fontSize: 12, color: Colors.GRAY1 },
    section: {
      paddingInline: 14,
    },
  };

  const config = {
    name: "Markit Feedback",
    email: "hello@markitai.com",
  };

  const recentSubscription = mostRecentSubscription(accountData);
  const subscriptionPlan = useMemo(
    () => mostRecentSubscriptionPlan(accountData, testStripe(accountData.uid)),
    [accountData]
  );

  const trialSubscription = useMemo(
    () => accountData.customer.state === "TRIAL",
    [accountData.customer.state]
  );

  const userDataToShow = useMemo(
    () => teamMemberAccountData || accountData,
    [teamMemberAccountData, accountData]
  );

  const getSubscription = useCallback(() => {
    dispatch(notificationActions.setMarkitPlusModal(PlanTypeV4.ESSENTIAL.name));
  }, [dispatch]);

  const changeSubscription = useCallback(() => {
    navigate("/subscription", { state: { changePlan: true } });
    window.scrollTo(0, 0);
  }, [navigate]);

  const navigateCancelOnPress = useCallback(() => {
    // TODO: Account for downgrading
    navigate("/subscription/cancel");
    window.scrollTo(0, 0);
  }, [navigate]);

  const navigateBillingOnPress = useCallback(() => {
    if (setTabValue) {
      setTabValue(1);
    } else {
      onCreatorPanelNavigatePath(navigate, NavigationId.HOME_SETTINGS, 1);
      window.scrollTo(0, 0);
    }
  }, [navigate, setTabValue]);

  const unfreezeSubscription = useCallback(() => {
    navigate("/subscription/freeze");
    window.scrollTo(0, 0);
  }, [navigate]);

  const visitDocs = useCallback(() => {
    window.open(MARKIT_DOCS);
  }, []);

  const shareProfileMobile = useCallback(async () => {
    await onShareLink("Share Markit with your friends!", MARKITAI);
  }, []);

  const shareFeedback = useCallback(() => {
    window.open(
      `mailto:${config.email}?subject=${config.name}&body=${feedbackText}`,
      "_blank"
    );
  }, [config.email, config.name, feedbackText]);

  const manageStripe = useCallback(async () => {
    if (readyForPayment) {
      navigate(NavigationId.HOME_PAYOUTS);
    } else {
      setConnectStripeModal(true);
    }
  }, [readyForPayment, navigate]);

  const changePhoneEmail = useCallback(() => {
    if (isGoogleAuthUser()) {
      setAlertText({
        heading: "This is a Google Auth account.",
        subHeading:
          "Please contact support to receive help changing your email.",
      });
    } else {
      setChangeModal(true);
    }
  }, []);

  const renderSelectItem = useCallback(
    (title: string, onPress: () => void, loading?: boolean) => (
      <div className="AlignedRowSpacedSelect" onClick={onPress}>
        <span className="bodyMedium">{title}</span>
        {loading ? (
          <div style={{ paddingRight: 2 }}>
            <CircularProgress size={22} style={{ color: Colors.GRAY1 }} />
          </div>
        ) : (
          <Icon
            icon="mdi:chevron-right"
            height={26}
            style={{ color: Colors.GRAY1 }}
          />
        )}
      </div>
    ),
    []
  );

  const renderShareOptions = useMemo(
    () => (
      <>
        <RWebShare data={{ url: MARKITAI }}>
          <div
            className="AlignedRowSpacedSelect"
            onClick={isDesktop ? undefined : shareProfileMobile}
          >
            <span className="bodyMedium">Share Markit</span>
            <Icon
              icon="mdi:chevron-right"
              height={26}
              style={{ color: Colors.GRAY1 }}
            />
          </div>
        </RWebShare>
        {isDesktop
          ? renderSelectItem("Share Feedback", () => setFeedbackModalOpen(true))
          : null}
      </>
    ),
    [renderSelectItem, shareProfileMobile]
  );

  return (
    <>
      <div
        className={isDesktop ? "RowNormal" : "ColumnNormal"}
        style={{
          gap: isDesktop ? 20 : 14,
          alignItems: isDesktop ? "flex-start" : undefined,
        }}
      >
        <StandardBorderedContainer
          containerStyles={{ ...styles.container, flex: 1 }}
        >
          <div className="AlignedRow" style={{ ...styles.section, gap: 7 }}>
            <span className="AboutSubtitle">Your Account</span>
            <Icon icon="mdi:account-circle-outline" height={20} />
          </div>
          <hr style={{ marginBlock: 14 }} />
          {teamMemberAccountData && teamMemberData ? (
            <>
              <div
                className={isDesktop ? "AlignedRowSpaced" : "ColumnNormal"}
                style={{ ...styles.section, gap: 14 }}
              >
                <div
                  className="AlignedRow"
                  style={{ gap: isDesktop ? 30 : 14 }}
                >
                  <ProfilePic
                    user={teamMemberAccountData}
                    size={isDesktop ? 100 : 75}
                    borderStyle={`0.5px solid ${Colors.GRAY2}`}
                  />
                  <div className="ColumnNormal" style={{ gap: 7 }}>
                    <span
                      style={{ fontSize: isDesktop ? 20 : 14, fontWeight: 500 }}
                    >
                      {teamMemberAccountData.fullName}
                    </span>
                    <span
                      className={isDesktop ? "bodySubtext" : "smallBodySubtext"}
                    >
                      {teamMemberData.role === TeamMemberRole.FULL_ACCESS
                        ? "Full Admin"
                        : "Support"}
                    </span>
                  </div>
                </div>
                <div>
                  <RectangleButton
                    buttonLabel="Edit Profile"
                    onPress={() => setEditingProfile(true)}
                    altColor={Colors.WHITE1}
                    altTextColor={Colors.BLACK}
                    containerStyles={{
                      borderRadius: 12,
                      paddingBlock: 14,
                      paddingInline: 40,
                      border: `0.5px solid ${Colors.GRAY2}`,
                    }}
                  />
                </div>
              </div>
              <hr style={{ marginBlock: 14 }} />
            </>
          ) : null}
          <div className="ColumnNormal" style={{ ...styles.section, gap: 20 }}>
            <div className="AlignedRow" style={{ gap: 14 }}>
              <span className="bodySubtextMedium">Username</span>
              <span className="bodyMedium">{userDataToShow.username}</span>
            </div>
            <div className="AlignedRow" style={{ gap: 14 }}>
              <span className="bodySubtextMedium">
                {userDataToShow.email ? "Email" : "Phone Number"}
              </span>
              <span className="bodyMedium">
                {userDataToShow.email
                  ? userDataToShow.email
                  : userDataToShow.phoneNumber}
              </span>
            </div>
          </div>
          <hr style={{ marginBlock: 14 }} />
          {teamMemberAccountData ? (
            <div style={styles.section}>
              {renderSelectItem("Visit Docs", visitDocs)}
            </div>
          ) : (
            <>
              <div
                className="ColumnNormal"
                style={{ ...styles.section, gap: 5 }}
              >
                {accountData.customer.state === "FROZEN" ? (
                  <div
                    style={{
                      borderRadius: 8,
                      padding: 10,
                      marginBlock: 5,
                      backgroundColor: Colors.BLACK,
                    }}
                    className={"AlignedRowSpacedSelect"}
                    onClick={unfreezeSubscription}
                  >
                    <div className="ColumnNormal" style={{ gap: 3 }}>
                      <span
                        className="bodyMedium"
                        style={{ color: Colors.WHITE }}
                      >
                        Unfreeze Account
                      </span>
                      <span style={{ fontSize: 11, color: Colors.GRAY2 }}>
                        You must unfreeze your account to use Markit+
                      </span>
                    </div>
                    <Icon
                      icon="mdi:chevron-right"
                      height={26}
                      style={{ color: Colors.WHITE }}
                    />
                  </div>
                ) : accountData.customer.state === "PAYMENT_FAILED" ? (
                  <div style={{ marginBlock: 5 }}>
                    <PaymentFailedButtonModal
                      isVisible={paymentFailedVisible}
                      setIsVisible={setPaymentFailedVisible}
                      button={
                        <PaymentFailedButton
                          type="Creator"
                          onPress={() => setPaymentFailedVisible(true)}
                        />
                      }
                      location="Profile Account"
                    />
                  </div>
                ) : (
                  <>
                    <SubscriptionPlanLabelItem
                      planName={
                        accountData.customer.state === "TRIAL"
                          ? "Free Trial"
                          : subscriptionPlan
                            ? subscriptionPlan.name
                            : "Free Plan"
                      }
                    />
                    {accountData.customer.cancelAtEnd && recentSubscription ? (
                      <span style={styles.subtext}>
                        Cancels: {GetLongDate(recentSubscription.end)}
                      </span>
                    ) : null}
                    {accountData.customer.state === "INACTIVE" ? (
                      <div style={{ marginBlock: 5 }}>
                        <GradientButton
                          title="Get Markit+"
                          subtext={
                            <span style={{ fontSize: 11, color: Colors.WHITE }}>
                              Get a unique phone number and more for $10
                            </span>
                          }
                          onPress={getSubscription}
                          gradientCss="blueGradient"
                        />
                      </div>
                    ) : null}
                  </>
                )}
              </div>
              <hr style={{ marginBlock: 10 }} />
              <div
                className="ColumnNormal"
                style={{ ...styles.section, gap: 14 }}
              >
                {hasSubscription(accountData) &&
                !isBusinessSubscription(accountData)
                  ? renderSelectItem("Upgrade", changeSubscription)
                  : null}
                {!isDesktop && hasSubscription(accountData)
                  ? renderSelectItem(
                      "Cancel " +
                        (trialSubscription ? "Trial" : "Subscription"),
                      navigateCancelOnPress
                    )
                  : null}
                {hasSubscription(accountData)
                  ? renderSelectItem("Manage Billing", navigateBillingOnPress)
                  : null}
                {renderSelectItem("Visit Docs", visitDocs)}
              </div>
            </>
          )}
        </StandardBorderedContainer>
        {teamMemberAccountData ? (
          <div className="ColumnNormal" style={{ flex: 1 }}>
            <StandardBorderedContainer
              containerStyles={{ ...styles.container, ...styles.section }}
            >
              <div className="ColumnNormal" style={{ gap: 14 }}>
                {accountData.phoneNumber === "" && !isGoogleAuthUser()
                  ? renderSelectItem("Change Password", () =>
                      setPasswordModal(true)
                    )
                  : null}
                {renderShareOptions}
              </div>
            </StandardBorderedContainer>
          </div>
        ) : (
          <div className="ColumnNormal" style={{ flex: 1, gap: 20 }}>
            <StandardBorderedContainer
              containerStyles={{ ...styles.container, ...styles.section }}
            >
              <div className="ColumnNormal" style={{ gap: 14 }}>
                {renderSelectItem(
                  `Change ${
                    accountData.phoneNumber !== "" ? "Phone Number" : "Email"
                  }`,
                  changePhoneEmail
                )}
                {accountData.isAdmin
                  ? renderSelectItem(
                      `Convert to ${
                        accountData.phoneNumber !== "" ? "Email" : "Phone"
                      } (admin only)`,
                      () => setConvertModal(true)
                    )
                  : null}
                {accountData.email && !isGoogleAuthUser()
                  ? renderSelectItem("Change Password", () =>
                      setPasswordModal(true)
                    )
                  : null}
              </div>
            </StandardBorderedContainer>
            <StandardBorderedContainer
              containerStyles={{ ...styles.container, ...styles.section }}
            >
              <div className="ColumnNormal" style={{ gap: 14 }}>
                {renderSelectItem("Manage Stripe", manageStripe)}
                {renderShareOptions}
                {renderSelectItem("Delete Account", () =>
                  setDeleteAccountModal(true)
                )}
              </div>
            </StandardBorderedContainer>
          </div>
        )}
      </div>
      {connectStripeModal ? (
        <EnablePaidTicketsModal
          isVisible={connectStripeModal}
          setIsVisible={setConnectStripeModal}
        />
      ) : null}
      {feedbackModalOpen ? (
        <PopupModalContainer
          headerComp={
            <div className="ColumnNormal" style={{ gap: 4 }}>
              <span className="AboutSubtitle">Share Feedback</span>
              <span style={{ color: Colors.GRAY1, fontSize: 14 }}>
                Share any issues or thoughts with the Markit team.
              </span>
            </div>
          }
          valueComp={
            <div className="ColumnNormal" style={{ gap: 14 }}>
              <div className="descriptionTextAreaContainer">
                <textarea
                  value={feedbackText}
                  placeholder={"Write your feedback here..."}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  className="descriptionTextArea"
                  maxLength={2000}
                />
                <div
                  className="AlignedRow"
                  style={{ marginTop: 14, justifyContent: "space-between" }}
                >
                  <h4
                    className="bodyMedium"
                    style={{
                      color: Colors.GRAY1,
                    }}
                  >
                    {feedbackText.length}/2000
                  </h4>
                </div>
              </div>
              <RectangleButton
                buttonLabel="Share"
                altColor={Colors.BLACK}
                disabled={feedbackText.length === 0}
                onPress={shareFeedback}
              />
            </div>
          }
          closeModal={() => {
            setFeedbackModalOpen(false);
            setFeedbackText("");
          }}
        />
      ) : null}
      <EditProfileConvertModal
        convertModal={convertModal}
        setConvertModal={setConvertModal}
        changeModal={changeModal}
        setChangeModal={setChangeModal}
        passwordModal={passwordModal}
        setPasswordModal={setPasswordModal}
      />
      {editingProfile ? (
        <ProfilePageEditProfile
          isVisible={editingProfile}
          setIsVisible={setEditingProfile}
        />
      ) : null}
      <AlertContainer
        headerComp={alertText.heading}
        subHeaderComp={
          alertText.subHeading !== "" ? alertText.subHeading : undefined
        }
        closeModal={() => setAlertText({ heading: "", subHeading: "" })}
        hideModal={alertText.heading === "" && alertText.subHeading === ""}
      />
      {deleteAccountModal ? (
        <DeleteAccountModal
          isVisible={deleteAccountModal}
          setIsVisible={setDeleteAccountModal}
        />
      ) : null}
    </>
  );
};

export default ProfileAccountDetails;
